import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Gift, Clock4, CheckCircle2, Info, CalendarDays, ShieldCheck, ListChecks } from "lucide-react";
import { SWEEPSTAKE, trDate, isActiveWindow, isUpcomingWindow, isEndedWindow } from "data/sweepstake";

function useCountdown(targetISO: string) {
  const target = useMemo(() => Date.parse(targetISO), [targetISO]);
  const [left, setLeft] = useState(Math.max(target - Date.now(), 0));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(target - Date.now(), 0)), 1000);
    return () => clearInterval(id);
  }, [target]);
  const s = Math.floor(left / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    mins: Math.floor((s % 3600) / 60),
    secs: s % 60,
    done: left <= 0,
  };
}

export default function SweepstakesPage() {
  const navigate = useNavigate();

  const active = isActiveWindow();
  const upcoming = isUpcomingWindow();
  const ended = isEndedWindow();

  const countdownTarget = upcoming ? SWEEPSTAKE.entryStartAt : SWEEPSTAKE.entryEndAt;
  const { days, hours, mins, secs } = useCountdown(countdownTarget);

  return (
    <div className="bg-background text-foreground min-h-dvh">
      {/* HERO */}
      <section className="relative h-[42vh] min-h-[320px] w-full overflow-hidden">
        <img src={SWEEPSTAKE.heroImage} alt="Çekiliş" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center gap-3 text-center">
          <h1 className="text-white text-4xl sm:text-5xl font-extrabold drop-shadow">
            {SWEEPSTAKE.title}
          </h1>
          <p className="text-white/90 text-sm sm:text-base max-w-2xl">
            {SWEEPSTAKE.subtitle}
          </p>

          {/* Geri sayım rozeti */}
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm text-rose-700 shadow">
            <Clock4 className="h-4 w-4" />
            {upcoming ? "Başlangıca kalan:" : active ? "Bitişe kalan:" : "Çekiliş bitti"}
            {(active || upcoming) && (
              <b className="ml-2">{days}g {String(hours).padStart(2,"0")}s {String(mins).padStart(2,"0")}d {String(secs).padStart(2,"0")}s</b>
            )}
          </div>
        </div>
      </section>

      <main className="bg-gradient-to-b from-rose-50 via-pink-50 to-white">
        {/* Özet kutuları */}
        <section className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-4 md:grid-cols-3">
            <Card className="border-rose-100">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-rose-600" />
                  Kimler katılabilir?
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-neutral-700">
                Kampanya süresi içinde <b>herhangi bir paketi satın alan</b> tüm kullanıcılar otomatik olarak çekilişe katılır. Ek işlem gerekmez.
              </CardContent>
            </Card>

            <Card className="border-rose-100">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CalendarDays className="h-5 w-5 text-rose-600" />
                  Önemli Tarihler (TSİ)
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-neutral-700 space-y-1">
                <div>Başlangıç: <b>{trDate(SWEEPSTAKE.entryStartAt)}</b></div>
                <div>Bitiş: <b>{trDate(SWEEPSTAKE.entryEndAt)}</b></div>
                <div>Çekiliş: <b>{trDate(SWEEPSTAKE.drawAt)}</b></div>
              </CardContent>
            </Card>

            <Card className="border-rose-100">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-5 w-5 text-rose-600" />
                  Nasıl Yapılıyor?
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-neutral-700">
                Çekiliş, bilgisayar ortamında <b>rastgele</b> seçim yöntemiyle gerçekleştirilecek; kazananlar e-posta/telefon ile bilgilendirilecektir.
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Nasıl katılırım + Ödüller */}
        <section className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-8 lg:grid-cols-2">
            <Card className="border-rose-100">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ListChecks className="h-5 w-5 text-rose-600" />
                  Nasıl Katılırım?
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-neutral-700 space-y-2">
                <div>1. <b>Paket Seçin:</b> İhtiyacınıza uygun paketi belirleyin.</div>
                <div>2. <b>Satın Alın:</b> Ödeme adımını tamamlayın.</div>
                <div>3. <b>Otomatik Katılım:</b> Siparişinizle birlikte çekiliş hakkınız oluşur.</div>
                <div>4. <b>Sonuçları Bekleyin:</b> Çekiliş tarihinde kazananlar duyurulur.</div>
                <div className="pt-2">
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => navigate("/paketler")}>
                    Paketleri Gör
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-rose-100">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Gift className="h-5 w-5 text-rose-600" />
                  Ödüller
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-neutral-700">
                <ul className="grid gap-2">
                  {SWEEPSTAKE.prizes.map((p) => (
                    <li key={p.rank}>• <b>{p.title}</b> <span className="text-neutral-500">({p.note})</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Koşullar / Şartlar */}
        <section className="py-10">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border border-rose-100 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Info className="h-5 w-5 text-rose-600" />
                <h2 className="text-lg font-semibold">Çekiliş Koşulları ve Kampanya Şartları</h2>
              </div>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-neutral-700">
                {SWEEPSTAKE.terms.map((t, i) => <li key={i}>{t}</li>)}
              </ol>
              <p className="mt-4 text-xs text-neutral-500">
                Bu sayfada yer alan bilgiler pazarlama duyurusudur. Gerekli görülen durumlarda kampanya koşulları güncellenebilir.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
